import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { User, LoginRequest, LoginResponse } from '@rti/shared';
import { apiClient } from '@rti/shared';
import { trackUserEvent, IMPERSONATED_USER_KEY } from '@/utils/userTracking';
import { setGoogleAnalyticsUser, trackGoogleAnalyticsEvent } from '@/utils/googleAnalytics';
import { useNotificationToast } from '@/components/NotificationToastProvider';

const TOKEN_EXPIRY_TIME = 72 * 60 * 60 * 1000;
const TOKEN_KEY = 'access_token';
const USER_KEY = 'user';
const LAST_ACTIVITY_KEY = 'last_activity';

interface AuthContextType {
  user: User | null;
  realUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isTrialExpired: boolean;
  isSubscriptionLapsed: boolean;
  subscriptionAccessUntil: number | null;
  isPendingApproval: boolean;
  isImpersonating: boolean;
  login: (credentials: LoginRequest) => Promise<User>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateActivity: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
  startImpersonation: (targetUser: User) => void;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    return {
      user: null,
      realUser: null,
      isAuthenticated: false,
      isLoading: false,
      isTrialExpired: false,
      isSubscriptionLapsed: false,
      subscriptionAccessUntil: null,
      isPendingApproval: false,
      isImpersonating: false,
      login: async (): Promise<User> => { throw new Error('Not inside AuthProvider'); },
      logout: async () => { throw new Error('Not inside AuthProvider'); },
      refreshToken: async () => { throw new Error('Not inside AuthProvider'); },
      refreshUser: async () => { throw new Error('Not inside AuthProvider'); },
      updateActivity: () => { throw new Error('Not inside AuthProvider'); },
      updateUser: async () => { throw new Error('Not inside AuthProvider'); },
      startImpersonation: () => { throw new Error('Not inside AuthProvider'); },
      stopImpersonation: () => { throw new Error('Not inside AuthProvider'); },
    };
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const SESSION_COUNT_KEY = 'rs_session_count';

function getTrialDaysRemaining(user: User): number | null {
  if (!user.on_free_trial) return null;
  const ends = user.free_trial_ends_at
    ? Number(user.free_trial_ends_at)
    : user.free_trial_started_at && user.free_trial_duration_days
      ? Number(user.free_trial_started_at) + Number(user.free_trial_duration_days) * 86400
      : null;
  if (!ends || Number.isNaN(ends)) return null;
  const endsMs = ends > 1e12 ? ends : ends * 1000;
  return Math.max(0, Math.ceil((endsMs - Date.now()) / 86400000));
}

function applyClarityIdentity(user: User): void {
  try {
    const clarity = (window as any).clarity;
    if (!clarity) return;
    clarity('identify', user.user_id.toString(), undefined, undefined, `${user.first_name} ${user.last_name}`);
    clarity('set', 'email', user.email);
    clarity('set', 'company', user.company_name || 'Unknown');
    clarity('set', 'membership', user.effective_membership_id?.toString() || 'none');
    clarity('set', 'plan', (user as any).plan_name || (user as any).membership_name || 'unknown');
    clarity('set', 'is_employee', String(!!(user as any).is_employee || (user as any).is_admin));
    if (user.on_free_trial) {
      clarity('set', 'trial_status', 'active');
      const daysLeft = getTrialDaysRemaining(user);
      if (daysLeft !== null) clarity('set', 'trial_days_remaining', String(daysLeft));
    }
    const sessionCount = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '0', 10);
    clarity('set', 'session_count', String(sessionCount));
  } catch (clarityError) {
    console.warn('Clarity tracking error:', clarityError);
  }
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [realUser, setRealUser] = useState<User | null>(null);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem(IMPERSONATED_USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { showToast } = useNotificationToast();

  const user = impersonatedUser ?? realUser;
  const isImpersonating = impersonatedUser !== null;

  const isTokenExpired = (): boolean => {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return true;

    const timeSinceLastActivity = Date.now() - parseInt(lastActivity, 10);
    return timeSinceLastActivity > TOKEN_EXPIRY_TIME;
  };

  // Update last activity timestamp
  const updateActivity = (): void => {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  };

  // Clear all auth data
  const clearAuthData = (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    localStorage.removeItem(IMPERSONATED_USER_KEY);
    setRealUser(null);
    setImpersonatedUser(null);
    setIsAuthenticated(false);
    if (typeof window !== "undefined") {
      window.currentUser = null;
    }
  };

  const checkSubscriptionLapse = (currentUser: User | null): { lapsed: boolean; accessUntil: number | null } => {
    if (!currentUser) return { lapsed: false, accessUntil: null };
    const st = (currentUser.subscription_status || "").toLowerCase();
    if (!["canceled", "paused"].includes(st)) return { lapsed: false, accessUntil: null };
    const periodEnd = currentUser.subscription_current_period_end
      ? Number(currentUser.subscription_current_period_end)
      : null;
    if (!periodEnd) {
      return st === "paused"
        ? { lapsed: false, accessUntil: null }
        : { lapsed: true, accessUntil: null };
    }
    const periodEndMs = periodEnd > 1e12 ? periodEnd : periodEnd * 1000;
    return { lapsed: Date.now() > periodEndMs, accessUntil: periodEndMs };
  };

  const checkTrialExpiration = (currentUser: User | null): boolean => {
    if (!currentUser || !currentUser.on_free_trial) return false;
    const subStatus = (currentUser.subscription_status || "").toLowerCase();
    if (subStatus === "active" || subStatus === "trialing") return false;
    if (["canceled", "paused"].includes(subStatus) && currentUser.subscription_current_period_end) {
      const periodEnd = Number(currentUser.subscription_current_period_end);
      const periodEndMs = periodEnd > 1e12 ? periodEnd : periodEnd * 1000;
      if (Date.now() < periodEndMs) return false;
    }
    
    const ends = currentUser.free_trial_ends_at
      ? Number(currentUser.free_trial_ends_at)
      : currentUser.free_trial_started_at && currentUser.free_trial_duration_days
        ? Number(currentUser.free_trial_started_at) + Number(currentUser.free_trial_duration_days) * 86400
        : null;
    if (!ends || Number.isNaN(ends)) return false;
    const endsMs = ends > 1e12 ? ends : ends * 1000;
    return Date.now() > endsMs;
  };

  const { isTrialExpired, isSubscriptionLapsed, subscriptionAccessUntil, isPendingApproval } = useMemo(() => {
    const lapse = checkSubscriptionLapse(user);
    return {
      isTrialExpired: checkTrialExpiration(user),
      isSubscriptionLapsed: lapse.lapsed,
      subscriptionAccessUntil: lapse.accessUntil,
      isPendingApproval: !!user?.pending_company_approval,
    };
  }, [user]);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Listen for storage events to sync logout across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY && !e.newValue) {
        setRealUser(null);
        setImpersonatedUser(null);
        setIsAuthenticated(false);
      } else if (e.key === USER_KEY && !e.newValue) {
        setRealUser(null);
        setImpersonatedUser(null);
        setIsAuthenticated(false);
      } else if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
        const timeSinceLastActivity = Date.now() - parseInt(e.newValue, 10);
        if (timeSinceLastActivity > TOKEN_EXPIRY_TIME) {
          clearAuthData();
        } else {
          updateActivity();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Listen for auth-logout events from API client (401 errors)
  useEffect(() => {
    const handleAuthLogout = () => {
      console.log('[AuthContext] auth-logout event received - clearing auth data');
      clearAuthData();
    };

    window.addEventListener('auth-logout', handleAuthLogout);
    return () => window.removeEventListener('auth-logout', handleAuthLogout);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
    const intervalId = window.setInterval(() => {
      refreshUser();
    }, REFRESH_INTERVAL_MS);

    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshUser();
      }
    };
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleVisible);
    };
  }, [isAuthenticated]);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);

      if (token && !isTokenExpired()) {
        const userData = localStorage.getItem(USER_KEY);
        if (userData) {
          const parsedUser = JSON.parse(userData);
          setRealUser(parsedUser);
          setIsAuthenticated(true);
          if (typeof window !== "undefined") {
            window.currentUser = parsedUser;
            applyClarityIdentity(parsedUser);
            // Increment session count on resume
            const prev = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '0', 10);
            localStorage.setItem(SESSION_COUNT_KEY, String(prev + 1));
            trackUserEvent('session_resume', parsedUser.user_id.toString(), {
              email: parsedUser.email,
              company: parsedUser.company_name || 'Unknown',
              session_count: prev + 1,
            });
          }
          updateActivity();
        }
      } else if (token) {
        console.log('Token expired, clearing auth data');
        clearAuthData();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      clearAuthData();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials: LoginRequest): Promise<User> => {
    try {
      setIsLoading(true);
      const response: LoginResponse = await apiClient.login(credentials);

      if (response.token && response.user) {
        const { token, user } = response;

        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
        updateActivity();
        setRealUser(user);
        setIsAuthenticated(true);
        if (typeof window !== "undefined") {
          window.currentUser = user;
          const prev = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '0', 10);
          localStorage.setItem(SESSION_COUNT_KEY, String(prev + 1));
          applyClarityIdentity(user);
          setGoogleAnalyticsUser(user);
          trackGoogleAnalyticsEvent('login', { method: 'password' });
          trackUserEvent('user_login', user.user_id.toString(), {
            email: user.email,
            company: user.company_name || 'Unknown',
            membership: user.effective_membership_id?.toString() || 'none',
            on_trial: String(!!user.on_free_trial),
            session_count: prev + 1,
          });
        }

        try {
          const notifications = await apiClient.getInAppNotifications();
          const unread = notifications
            .filter((n) => !n.is_read)
            .sort((a, b) => b.id - a.id)
            .slice(0, 3);
          unread.forEach((n) => showToast(n.title, n.message));
        } catch {}

        return user;
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    const userId = user?.user_id?.toString();
    try {
      await trackUserEvent('user_logout', userId, { email: user?.email });
    } catch {}
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
    }
  };

  const refreshToken = async () => {
    try {
      const response = await apiClient.refreshToken();
      localStorage.setItem(TOKEN_KEY, response.accessToken);
      updateActivity();
    } catch (error) {
      console.error('Token refresh failed:', error);
      await logout();
    }
  };

  const refreshUser = async () => {
    try {
      const fresh = await apiClient.getUserData();
      setRealUser(fresh);
      localStorage.setItem(USER_KEY, JSON.stringify(fresh));
      if (typeof window !== "undefined") {
        window.currentUser = impersonatedUser ?? fresh;
      }
    } catch (error) {
      console.error('Refresh user failed:', error);
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      const response = await apiClient.updateUserProfile(userData);
      const updatedUser = { ...realUser, ...response };
      setRealUser(updatedUser);
      localStorage.setItem(USER_KEY, JSON.stringify(updatedUser));
      if (typeof window !== "undefined") {
        window.currentUser = impersonatedUser ?? updatedUser;
      }
    } catch (error) {
      console.error('Update user failed:', error);
      throw error;
    }
  };

  const startImpersonation = (targetUser: User): void => {
    localStorage.setItem(IMPERSONATED_USER_KEY, JSON.stringify(targetUser));
    setImpersonatedUser(targetUser);
    if (typeof window !== "undefined") {
      window.currentUser = targetUser;
    }
  };

  const stopImpersonation = (): void => {
    localStorage.removeItem(IMPERSONATED_USER_KEY);
    setImpersonatedUser(null);
    if (typeof window !== "undefined") {
      window.currentUser = realUser;
    }
  };

  const value: AuthContextType = {
    user,
    realUser,
    isAuthenticated,
    isLoading,
    isTrialExpired,
    isSubscriptionLapsed,
    subscriptionAccessUntil,
    isPendingApproval,
    isImpersonating,
    login,
    logout,
    refreshToken,
    refreshUser,
    updateActivity,
    updateUser,
    startImpersonation,
    stopImpersonation,
  };

  // Always keep window.currentUser in sync with user state
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.currentUser = user;
    }
  }, [user]);

  useEffect(() => {
    setGoogleAnalyticsUser(realUser);
  }, [realUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
